// sessionTree.ts — nests forked sessions under the session they branched
// from, for the sidebar. Pure: no React, no DOM.
//
//   buildSessionTree  a flat, ordered list → a forest of fork trees.
//   countDescendants  how many sessions sit under a node, at any depth.
//   someDescendant    whether any session under a node passes a test.
//   MAX_FORK_INDENT   the deepest level the sidebar still indents.
//
// The list comes from the manager, so its lineage is trusted only as far
// as it is consistent: a parent that isn't in the list (deleted, or in
// another workspace's group) makes the fork a root rather than hiding
// it, and a cycle — which a correct store never produces, but one bad
// row must not make the sidebar spin — is broken at the member that
// appears first in the input.

export interface ForkLike {
  id: string;
  /** The session this one was forked from; empty or absent for a root. */
  parent_session_id?: string;
  /** The parent's event seq the fork branched at. */
  fork_seq?: number;
}

export interface SessionTreeNode<T> {
  session: T;
  /** True nesting depth: 0 for a root. */
  depth: number;
  /** Depth as drawn — capped at MAX_FORK_INDENT so titles keep room. */
  indent: number;
  children: SessionTreeNode<T>[];
}

/**
 * Forks of forks past this depth line up at it instead of stepping
 * further in. Three steps of indent is about all a sidebar the user can
 * drag down to a couple of hundred pixels can spend before the titles
 * are squeezed to an ellipsis.
 */
export const MAX_FORK_INDENT = 3;

/**
 * Nest `rows` into fork trees. Roots keep the input order (the sidebar's
 * recency order); siblings are ordered by fork seq — where in the parent's
 * conversation each branched off — falling back to input order for forks
 * taken at the same point, as a swarm's are.
 */
export function buildSessionTree<T extends ForkLike>(
  rows: readonly T[],
  maxIndent: number = MAX_FORK_INDENT,
): SessionTreeNode<T>[] {
  const index = new Map<string, number>();
  rows.forEach((r, i) => {
    if (!index.has(r.id)) index.set(r.id, i);
  });

  // A parent link counts only if it names another row in the list.
  const parent = new Map<string, string>();
  for (const r of rows) {
    const p = r.parent_session_id;
    if (p && p !== r.id && index.has(p)) parent.set(r.id, p);
  }

  // Break cycles: walk up from each row; a walk that revisits a node has
  // found a loop, and the loop's earliest member in the input becomes a
  // root. Repeat from the same row until its walk reaches a root.
  for (const r of rows) {
    for (;;) {
      const seen: string[] = [];
      let cur: string | undefined = r.id;
      while (cur !== undefined && !seen.includes(cur)) {
        seen.push(cur);
        cur = parent.get(cur);
      }
      if (cur === undefined) break;
      const loop = seen.slice(seen.indexOf(cur));
      const first = loop.reduce((a, b) => (index.get(a)! <= index.get(b)! ? a : b));
      parent.delete(first);
    }
  }

  const byId = new Map<string, T>();
  const kids = new Map<string, T[]>();
  const roots: T[] = [];
  for (const r of rows) {
    if (byId.has(r.id)) continue; // a duplicated row is drawn once
    byId.set(r.id, r);
    const p = parent.get(r.id);
    if (p === undefined) {
      roots.push(r);
    } else {
      const list = kids.get(p) ?? [];
      list.push(r);
      kids.set(p, list);
    }
  }

  const bySeq = (a: T, b: T) =>
    (a.fork_seq ?? 0) - (b.fork_seq ?? 0) || index.get(a.id)! - index.get(b.id)!;

  const build = (s: T, depth: number): SessionTreeNode<T> => ({
    session: s,
    depth,
    indent: Math.min(depth, maxIndent),
    children: (kids.get(s.id) ?? []).slice().sort(bySeq).map((c) => build(c, depth + 1)),
  });
  return roots.map((r) => build(r, 0));
}

/** Every session under `node`, at any depth. */
export function countDescendants<T>(node: SessionTreeNode<T>): number {
  return node.children.reduce((n, c) => n + 1 + countDescendants(c), 0);
}

/** Whether any session under `node` (not `node` itself) passes `test`. */
export function someDescendant<T>(
  node: SessionTreeNode<T>,
  test: (session: T) => boolean,
): boolean {
  return node.children.some((c) => test(c.session) || someDescendant(c, test));
}
